import { useState } from 'react'
import { createPortal } from 'react-dom'
import { useStore } from '../store'
import { DEVICE_ID, relativeTime } from '../lib/device'
import {
  PROVIDER_LABELS, authErrorMessage, linkPassword, linkProvider, linkedProviders,
  signOut, unlinkProvider,
} from '../firebase'
import { DOWNLOADS, FIRST_RUN_NOTE } from '../lib/downloads'

/** The Electron shell exposes this; there is no point offering it a download. */
const isDesktopApp = typeof window !== 'undefined' && 'electronShell' in window

export function Settings({ onClose }: { onClose: () => void }) {
  const store = useStore()
  const [busy, setBusy] = useState(false)
  const [linked, setLinked] = useState<string[]>(() => linkedProviders())
  const [linkError, setLinkError] = useState<string | null>(null)

  const withLinks = async (fn: () => Promise<void>) => {
    setLinkError(null)
    try { await fn() } catch (e) { setLinkError(authErrorMessage(e)) }
    setLinked(linkedProviders())
  }
  const devices = [...store.devices]
    .filter((d) => !d.revokedAt)
    .sort((a, b) => (a.id === DEVICE_ID ? -1 : b.id === DEVICE_ID ? 1 : b.lastSeenAt - a.lastSeenAt))

  const signOutEverywhere = async () => {
    const ok = await store.ask({
      title: 'Sign out on every device?',
      message: 'Including this one. You will need to sign in again everywhere.',
      confirmLabel: 'Sign Out All', danger: true,
    })
    if (!ok) return
    setBusy(true)
    try { await store.signOutEverywhere() } finally { setBusy(false) }
  }

  // Portalled to <body>: .pane-sidebar sets backdrop-filter, which makes it a
  // containing block for position:fixed children — the sheet would otherwise be
  // trapped inside the 224px sidebar instead of covering the window.
  return createPortal(
    <div className="sheet-scrim" onMouseDown={onClose}>
      <div className="sheet" onMouseDown={(e) => e.stopPropagation()} role="dialog" aria-label="Settings">
        <div className="sheet-head">
          <h2>Settings</h2>
          <button className="sheet-done" onClick={onClose}>Done</button>
        </div>

        <div className="sheet-body">
          <div className="sheet-section">Account</div>
          <div className="sheet-row">
            {store.user?.photoURL && <img className="sheet-avatar" src={store.user.photoURL} alt="" referrerPolicy="no-referrer" />}
            <div className="sheet-row-main">
              <div className="sheet-row-title">{store.user?.displayName || 'Signed in'}</div>
              <div className="sheet-row-sub">{store.user?.email}</div>
            </div>
            <button className="sheet-btn" onClick={() => void signOut()}>Sign Out</button>
          </div>

          <div className="sheet-section">Sign-in methods</div>
          {(['google.com', 'apple.com', 'password'] as const).map((id) => {
            const on = linked.includes(id)
            return (
              <div className="sheet-row" key={id}>
                <div className="sheet-row-main">
                  <div className="sheet-row-title">{PROVIDER_LABELS[id]}</div>
                  <div className="sheet-row-sub">{on ? 'Connected' : 'Not connected'}</div>
                </div>
                {id === 'password' ? (
                  <button className="sheet-btn" disabled={on}
                          onClick={() => void withLinks(async () => {
                            const pw = prompt('Choose a password (at least 6 characters)')
                            if (pw) await linkPassword(pw)
                          })}>
                    {on ? 'Connected' : 'Add'}
                  </button>
                ) : on ? (
                  <button className="sheet-btn danger" disabled={linked.length < 2}
                          onClick={() => void withLinks(() => unlinkProvider(id))}>
                    Disconnect
                  </button>
                ) : (
                  <button className="sheet-btn" onClick={() => void withLinks(() => linkProvider(id))}>
                    Connect
                  </button>
                )}
              </div>
            )
          })}
          <p className="sheet-note">
            Connecting a method lets you sign in either way and still reach the same
            notes. The last remaining method can’t be disconnected.
          </p>
          {linkError && <p className="sheet-note" style={{ color: 'var(--danger)' }}>{linkError}</p>}

          {/* Only worth offering where you are not already running it. */}
          {!isDesktopApp && (
            <>
              <div className="sheet-section">Get the apps</div>
              {DOWNLOADS.map((d) => (
                <div className="sheet-row" key={d.platform}>
                  <div className="sheet-row-main">
                    <div className="sheet-row-title">{d.platform}</div>
                    <div className="sheet-row-sub">{d.note}</div>
                  </div>
                  <a className="sheet-btn" href={d.url} download>Download</a>
                </div>
              ))}
              <p className="sheet-note">{FIRST_RUN_NOTE}</p>
            </>
          )}

          <div className="sheet-section">Appearance</div>
          <div className="theme-picker" role="radiogroup" aria-label="Appearance">
            {(['system', 'light', 'dark'] as const).map((t) => (
              <button
                key={t}
                role="radio"
                aria-checked={store.theme === t}
                className={`theme-option ${store.theme === t ? 'on' : ''}`}
                onClick={() => store.setTheme(t)}
              >
                <span className={`theme-swatch ${t}`} aria-hidden />
                {t === 'system' ? 'System' : t === 'light' ? 'Light' : 'Dark'}
              </button>
            ))}
          </div>

          <div className="sheet-section">
            Devices
            <span className="sheet-count">{devices.length}</span>
          </div>

          {devices.length === 0 && <div className="sheet-empty">No other devices signed in.</div>}

          {devices.map((d) => {
            const isThis = d.id === DEVICE_ID
            return (
              <div className="sheet-row" key={d.id}>
                <div className="sheet-row-main">
                  <div className="sheet-row-title">
                    {d.label}
                    {isThis && <span className="sheet-badge">This device</span>}
                  </div>
                  <div className="sheet-row-sub">
                    {d.platform} · {relativeTime(d.lastSeenAt)}
                  </div>
                </div>
                <button
                  className="sheet-btn danger"
                  onClick={() => {
                    if (isThis) { void signOut(); return }
                    void store.ask({ title: `Sign out “${d.label}”?`, confirmLabel: 'Revoke', danger: true })
                      .then((ok) => { if (ok) store.revokeDevice(d.id) })
                  }}
                >
                  {isThis ? 'Sign Out' : 'Revoke'}
                </button>
              </div>
            )
          })}

          <p className="sheet-note">
            Revoking signs that device out the next time it reaches the network. To
            cut off a device you no longer control, use Sign Out All Devices — that
            one is enforced by the security rules on Google’s servers and cannot be
            bypassed.
          </p>

          <button className="sheet-wide danger" disabled={busy} onClick={() => void signOutEverywhere()}>
            {busy ? 'Signing out…' : 'Sign Out All Devices'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
